import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import {
  type StripeEnv,
  createStripeClient,
  getStripeErrorMessage,
  SERVICE_FEE_PRICE_ID,
  ONE_TIME_GIFT_PRICE_ID,
} from "@/lib/stripe.server";

type CheckoutSessionResult = { clientSecret: string } | { error: string };
type PortalSessionResult = { url: string } | { error: string };
type CancelResult = { canceled: true; effectiveAt: string } | { error: string };

// Legacy $49.99 / $125 plans are retired. Only the live catalog keys are
// accepted for new membership checkouts.
const ALLOWED_MEMBERSHIP_PRICES = new Set([
  "vip_autopilot_monthly",
  "monthly_floral_club_monthly",
  "pro_monthly",
]);

export async function resolveOrCreateCustomer(
  stripe: ReturnType<typeof createStripeClient>,
  options: { email?: string; userId: string },
): Promise<string> {
  if (!/^[a-zA-Z0-9_-]+$/.test(options.userId)) throw new Error("Invalid userId");

  const found = await stripe.customers.search({
    query: `metadata['userId']:'${options.userId}'`,
    limit: 1,
  });
  if (found.data.length) return found.data[0].id;

  if (options.email) {
    const existing = await stripe.customers.list({ email: options.email, limit: 1 });
    if (existing.data.length) {
      const customer = existing.data[0];
      if (customer.metadata?.userId !== options.userId) {
        await stripe.customers.update(customer.id, {
          metadata: { ...customer.metadata, userId: options.userId },
        });
      }
      return customer.id;
    }
  }

  const created = await stripe.customers.create({
    ...(options.email && { email: options.email }),
    metadata: { userId: options.userId },
  });
  return created.id;
}

async function resolveStripePriceId(
  stripe: ReturnType<typeof createStripeClient>,
  lookupKey: string,
): Promise<string> {
  const prices = await stripe.prices.list({ lookup_keys: [lookupKey] });
  if (!prices.data.length) throw new Error(`Price not found: ${lookupKey}`);
  return prices.data[0].id;
}

/**
 * Membership checkout: subscription for the chosen tier + a recurring $5/mo
 * logistics & curation fee as a second line item.
 */
export const createMembershipCheckout = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: {
    priceId: string;
    returnUrl: string;
    environment: StripeEnv;
  }) => {
    if (!ALLOWED_MEMBERSHIP_PRICES.has(data.priceId)) throw new Error("Invalid priceId");
    return data;
  })
  .handler(async ({ data, context }): Promise<CheckoutSessionResult> => {
    try {
      const { userId, supabase } = context;
      const { data: userRes } = await supabase.auth.getUser();
      const email = userRes.user?.email ?? undefined;

      const stripe = createStripeClient(data.environment);
      const [membershipPriceId, feePriceId] = await Promise.all([
        resolveStripePriceId(stripe, data.priceId),
        resolveStripePriceId(stripe, SERVICE_FEE_PRICE_ID),
      ]);

      const customerId = await resolveOrCreateCustomer(stripe, { email, userId });

      const session = await stripe.checkout.sessions.create({
        line_items: [
          { price: membershipPriceId, quantity: 1 },
          { price: feePriceId, quantity: 1 },
        ],
        mode: "subscription",
        ui_mode: "embedded_page",
        return_url: data.returnUrl,
        customer: customerId,
        ...(data.environment === "live" && { automatic_tax: { enabled: true } }),
        customer_update: { address: "auto", name: "auto" },
        metadata: { userId, plan_price_id: data.priceId },
        subscription_data: { metadata: { userId, plan_price_id: data.priceId } },
      });

      return { clientSecret: session.client_secret ?? "" };
    } catch (error) {
      return { error: getStripeErrorMessage(error) };
    }
  });

/**
 * One-time curated gift checkout for an additional (non-primary) recipient
 * milestone. Routed to curated manual fulfillment when the webhook fires.
 */
export const createOneTimeGiftCheckout = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: {
    recipientId: string;
    returnUrl: string;
    environment: StripeEnv;
  }) => {
    if (!/^[a-zA-Z0-9-]+$/.test(data.recipientId)) throw new Error("Invalid recipientId");
    return data;
  })
  .handler(async ({ data, context }): Promise<CheckoutSessionResult> => {
    try {
      const { userId, supabase } = context;
      const { data: recipient, error: rErr } = await supabase
        .from("recipients")
        .select("id, name")
        .eq("id", data.recipientId)
        .eq("user_id", userId)
        .maybeSingle();
      if (rErr || !recipient) throw new Error("Recipient not found");

      const { data: userRes } = await supabase.auth.getUser();
      const email = userRes.user?.email ?? undefined;

      const stripe = createStripeClient(data.environment);
      const priceId = await resolveStripePriceId(stripe, ONE_TIME_GIFT_PRICE_ID);
      const customerId = await resolveOrCreateCustomer(stripe, { email, userId });

      const session = await stripe.checkout.sessions.create({
        line_items: [{ price: priceId, quantity: 1 }],
        mode: "payment",
        ui_mode: "embedded_page",
        return_url: data.returnUrl,
        customer: customerId,
        customer_update: { address: "auto", name: "auto" },
        payment_intent_data: {
          description: `FLWR One-Time Gift: ${recipient.name}`,
          metadata: {
            userId,
            recipient_id: data.recipientId,
            kind: "one_time_upsell",
          },
        },
        metadata: {
          userId,
          recipient_id: data.recipientId,
          kind: "one_time_upsell",
        },
      });

      return { clientSecret: session.client_secret ?? "" };
    } catch (error) {
      return { error: getStripeErrorMessage(error) };
    }
  });

/**
 * Open the Stripe billing portal (payment methods, invoices).
 */
export const createBillingPortalSession = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { returnUrl: string; environment: StripeEnv }) => data)
  .handler(async ({ data, context }): Promise<PortalSessionResult> => {
    try {
      const { supabase, userId } = context;
      const { data: sub, error } = await supabase
        .from("subscriptions")
        .select("stripe_customer_id")
        .eq("user_id", userId)
        .eq("environment", data.environment)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error || !sub?.stripe_customer_id) throw new Error("No subscription found");

      const stripe = createStripeClient(data.environment);
      const portal = await stripe.billingPortal.sessions.create({
        customer: sub.stripe_customer_id,
        return_url: data.returnUrl,
      });
      return { url: portal.url };
    } catch (error) {
      return { error: getStripeErrorMessage(error) };
    }
  });

/**
 * Cancel the member's subscription at period end. Blocked strictly until
 * `committed_until` has passed. No early exits across either tier.
 */
export const cancelMembership = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { environment: StripeEnv }) => data)
  .handler(async ({ data, context }): Promise<CancelResult> => {
    try {
      const { supabase, userId } = context;
      const { data: sub, error } = await supabase
        .from("subscriptions")
        .select("stripe_subscription_id, tier, committed_until, current_period_end, status")
        .eq("user_id", userId)
        .eq("environment", data.environment)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error || !sub?.stripe_subscription_id) throw new Error("No active membership found");

      // Only the Curated tier has a 3-month commitment lock. Signature
      // members can cancel any time at period end.
      if (sub.tier === "curated" && sub.committed_until) {
        const until = new Date(sub.committed_until as string);
        const now = new Date();
        if (until > now) {
          const days = Math.ceil((until.getTime() - now.getTime()) / 86_400_000);
          const dateLabel = until.toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
          });
          return {
            error: `Your Curated membership is locked in for ${days} more day${days === 1 ? "" : "s"} (until ${dateLabel}) as part of your 3-month commitment.`,
          };
        }
      }

      const stripe = createStripeClient(data.environment);
      const updated = await stripe.subscriptions.update(sub.stripe_subscription_id as string, {
        cancel_at_period_end: true,
      });

      const item = updated.items?.data?.[0];
      const periodEnd = item?.current_period_end ?? (updated as any).current_period_end;
      return {
        canceled: true,
        effectiveAt: periodEnd ? new Date(periodEnd * 1000).toISOString() : new Date().toISOString(),
      };
    } catch (error) {
      return { error: getStripeErrorMessage(error) };
    }
  });
